import React from "react";
import { Link } from "react-router-dom";
import { Check, X, ShieldCheck, Server, RefreshCw, LifeBuoy, ArrowRight, Store } from "lucide-react";
import { Container, SectionHeading, PrimaryLink, SecondaryLink } from "@/components/ui-kit";
import { STEPS, WHAT_WE_HANDLE, WHAT_WE_DONT, PLANS, PH_AUDIENCE, getPlanPrice } from "@/data/content";
import { useRegion } from "@/hooks/useRegion";

export default function Home() {
  const { region, isPH, link } = useRegion();
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden" data-testid="home-hero">
        <div className="absolute inset-0">
          <img src="/images/background-banner.png" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0F172A] via-[#0F172A]/90 to-[#0F172A]/55" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A]/80 via-transparent to-[#0F172A]/40" />
        </div>
        <Container className="relative py-20 md:py-28 lg:py-32">
          <div className="max-w-2xl fade-up">
            <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 backdrop-blur-sm px-4 py-1.5 text-xs font-medium text-white">
              <ShieldCheck size={14} className="text-[#10B981]" />{" "}
              {isPH ? "Website responsibility for Philippine businesses" : "Websites built or taken over. One point of responsibility."}
            </span>
            <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white font-display text-balance drop-shadow-sm">
              We take responsibility for your website, so you don't have to.
            </h1>
            <p className="mt-6 text-lg sm:text-xl text-slate-200 leading-relaxed max-w-xl">
              {isPH
                ? "Own it yourself or let MySiteCare stay responsible. Build or take over a website with the level of hosting, maintenance, monitoring, backups, and covered technical responsibility you choose."
                : "MySiteCare builds and takes over business websites, with hosting, maintenance, monitoring, backups, and technical responsibility available according to the selected plan."}
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-4">
              <PrimaryLink to={link("/contact")} testId="hero-cta-primary">
                Tell Us About Your Website
              </PrimaryLink>
              <Link
                to={link("/plans")}
                data-testid="hero-cta-plans"
                className="inline-flex items-center justify-center gap-2 rounded-xl border-2 border-white/30 bg-white/5 backdrop-blur-sm px-7 py-3.5 text-sm font-semibold text-white hover:bg-white/15 transition-colors"
              >
                {isPH ? "View PH Plans" : "View Plans"}
              </Link>
            </div>
          </div>
        </Container>
      </section>

      {/* PH AUDIENCE */}
      {isPH && (
        <section className="py-20 md:py-24 bg-white" data-testid="ph-audience">
          <Container>
            <SectionHeading
              eyebrow="Who this is for"
              title="Built for Philippine businesses that need a website handled"
              subtitle="MySiteCare is for local operators who want their website to just work — without managing the technical side themselves."
              center
            />
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {PH_AUDIENCE.map((a) => (
                <div key={a} className="rounded-2xl border border-[#E2E8F0] bg-[#F8FAFC] p-5 text-center hover:shadow-md hover:-translate-y-1 transition-all">
                  <Store size={22} className="text-[#10B981] mx-auto" />
                  <p className="mt-3 text-sm font-medium text-[#0F172A]">{a}</p>
                </div>
              ))}
            </div>
          </Container>
        </section>
      )}

      {/* HOW IT WORKS */}
      <section className="py-20 md:py-24 bg-white" data-testid="how-it-works">
        <Container>
          <SectionHeading
            eyebrow="How it works"
            title="Simple. Calm. Controlled."
            subtitle="The selected plan decides what MySiteCare handles, what remains with you, and what happens when something goes wrong."
            center
          />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {STEPS.map((s) => (
              <div
                key={s.n}
                data-testid={`step-${s.n}`}
                className="relative rounded-2xl border border-[#E2E8F0] bg-white p-8 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all"
              >
                <span className="text-5xl font-bold font-display text-[#10B981]/15">{s.n}</span>
                <h3 className="mt-3 text-xl font-semibold font-display text-[#0F172A]">{s.title}</h3>
                <p className="mt-3 text-[#475569] leading-relaxed">{s.text}</p>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* WHAT WE HANDLE / DON'T */}
      <section className="py-20 md:py-24 bg-[#F8FAFC]" data-testid="what-we-do">
        <Container>
          <SectionHeading
            eyebrow="What MySiteCare is"
            title="Your website should not demand attention. It should just work."
            subtitle="We provide clearly defined website responsibility: who handles the website, what is covered, and what happens when something goes wrong."
          />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="rounded-2xl border border-[#E2E8F0] bg-white overflow-hidden shadow-sm">
              <div className="h-48 overflow-hidden bg-[#F8FAFC] flex items-center justify-center p-2">
                <img src="/images/what-we-do.png" alt="What we handle" className="max-w-full max-h-full object-contain" loading="lazy" />
              </div>
              <div className="p-8">
                <h3 className="text-lg font-semibold font-display text-[#0F172A] uppercase tracking-wide">What we handle</h3>
                <p className="mt-1 text-sm text-[#475569]">Responsibility depends on the selected plan and accepted scope.</p>
                <div className="mt-5 space-y-3">
                  {WHAT_WE_HANDLE.map((item) => (
                    <div key={item} className="flex items-start gap-3">
                      <span className="rounded-full bg-[#10B981]/10 p-1 mt-0.5">
                        <Check size={14} className="text-[#10B981]" />
                      </span>
                      <span className="text-sm text-[#1E293B]">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-[#E2E8F0] bg-white overflow-hidden shadow-sm">
              <div className="h-48 overflow-hidden bg-[#F8FAFC] flex items-center justify-center p-2">
                <img src="/images/what-we-dont-promise.png" alt="What we don't promise" className="max-w-full max-h-full object-contain" loading="lazy" />
              </div>
              <div className="p-8">
                <h3 className="text-lg font-semibold font-display text-[#0F172A] uppercase tracking-wide">What we do not promise</h3>
                <p className="mt-1 text-sm text-[#475569]">We are not a marketing agency. We do not promise:</p>
                <div className="mt-5 space-y-3">
                  {WHAT_WE_DONT.map((item) => (
                    <div key={item} className="flex items-start gap-3">
                      <span className="rounded-full bg-slate-100 p-1 mt-0.5">
                        <X size={14} className="text-[#94A3B8]" />
                      </span>
                      <span className="text-sm text-[#475569]">{item}</span>
                    </div>
                  ))}
                </div>
                <p className="mt-6 text-sm text-[#1E293B] font-medium">We handle the website work assigned to us. We do not run your business.</p>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* FEATURE STRIP */}
      <section className="py-16 bg-white border-y border-[#E2E8F0]">
        <Container>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { icon: Server, title: "Hosting & updates", text: "Managed for stability — not experimentation." },
              { icon: RefreshCw, title: "Backups & recovery", text: "Regular backups and disaster recovery." },
              { icon: ShieldCheck, title: "Monitoring", text: "Uptime and system-issue monitoring." },
              { icon: LifeBuoy, title: "One responsible party", text: "You message us. You don't troubleshoot." },
            ].map((f) => (
              <div key={f.title} className="text-center sm:text-left">
                <div className="inline-flex rounded-xl bg-[#10B981]/10 p-3">
                  <f.icon size={22} className="text-[#10B981]" />
                </div>
                <h4 className="mt-3 font-semibold font-display text-[#0F172A]">{f.title}</h4>
                <p className="mt-1 text-sm text-[#475569]">{f.text}</p>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* PLANS PREVIEW */}
      <section className="py-20 md:py-24 bg-[#F8FAFC]" data-testid="plans-preview">
        <Container>
          <SectionHeading
            eyebrow="Responsibility options"
            title="Choose your responsibility level"
            subtitle="This is not about features. It's about who is responsible when something goes wrong."
            center
          />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {PLANS.map((p) => {
              const pr = getPlanPrice(p.id, region);
              return (
                <Link
                  key={p.id}
                  to={link(`/plans/${p.slug}`)}
                  className={`block rounded-2xl bg-white p-6 transition-all hover:-translate-y-1 ${
                    p.highlight ? "border-2 border-[#10B981] shadow-md" : "border border-[#E2E8F0] shadow-sm hover:shadow-md"
                  }`}
                >
                  <span className="text-xs font-semibold uppercase tracking-wide text-[#94A3B8]">{p.type}</span>
                  <h3 className="mt-1 text-lg font-bold font-display text-[#0F172A]">{p.name}</h3>
                  <div className="mt-3 flex items-baseline gap-1">
                    <span className="text-2xl font-bold font-display text-[#0F172A]">{pr.price}</span>
                    <span className="text-sm text-[#475569]">{pr.period}</span>
                  </div>
                  <p className="mt-3 text-sm text-[#475569] line-clamp-3">{p.summary}</p>
                </Link>
              );
            })}
          </div>
          <div className="mt-12 text-center">
            <PrimaryLink to={link("/plans")} testId="plans-preview-cta">
              {isPH ? "View all PH plans" : "View all plans"}
            </PrimaryLink>
          </div>
        </Container>
      </section>

      {/* FINAL CTA */}
      <section className="py-20 md:py-24 bg-white" data-testid="home-final-cta">
        <Container>
          <div className="rounded-3xl bg-[#0F172A] px-8 py-16 md:px-16 text-center relative overflow-hidden">
            <div className="absolute inset-0 opacity-20">
              <img src="/images/what-my-site-care-is.png" alt="" className="w-full h-full object-cover" />
            </div>
            <div className="relative">
              <h2 className="text-3xl sm:text-4xl font-bold font-display text-white text-balance">
                Ready to stop thinking about your website?
              </h2>
              <p className="mt-4 text-lg text-slate-300 max-w-2xl mx-auto">
                Choose your responsibility level — or message us to talk. No pressure. No sales scripts. Just clarity.
              </p>
              <div className="mt-9 flex flex-col sm:flex-row gap-4 justify-center">
                <PrimaryLink to={link("/contact")} testId="final-cta-contact">
                  Contact Us
                </PrimaryLink>
                <Link
                  to={link("/plans")}
                  data-testid="final-cta-plans"
                  className="inline-flex items-center justify-center gap-2 rounded-xl border-2 border-white/20 px-7 py-3.5 text-sm font-semibold text-white hover:bg-white/5 transition-colors"
                >
                  {isPH ? "View PH Plans" : "View Plans"} <ArrowRight size={16} />
                </Link>
              </div>
            </div>
          </div>
        </Container>
      </section>
    </div>
  );
}
